/* Effective resolution of artwork at its print size. */

import type { BuilderSettings } from './config.js';
import type { AssetInfo } from './model.js';
import { MM_PER_IN } from './units.js';

export type Quality = 'good' | 'fair' | 'low' | 'vector';

export interface DpiResult {
  dpi: number;          // Infinity for vectors
  quality: Quality;
}

/** Trimmed source pixels of an asset (what is actually printed). */
export function trimmedPx(asset: Pick<AssetInfo, 'widthPx' | 'heightPx' | 'trim'>, trim: boolean): { w: number; h: number } {
  if (!trim) return { w: asset.widthPx, h: asset.heightPx };
  return { w: Math.max(1, asset.widthPx * asset.trim.w), h: Math.max(1, asset.heightPx * asset.trim.h) };
}

export function effectiveDpi(
  asset: Pick<AssetInfo, 'kind' | 'widthPx' | 'heightPx' | 'trim'>,
  trim: boolean,
  widthMm: number,
  heightMm: number,
  settings: Pick<BuilderSettings, 'dpiGood' | 'dpiFair'>
): DpiResult {
  if (asset.kind !== 'raster') return { dpi: Infinity, quality: 'vector' };
  const px = trimmedPx(asset, trim);
  const dx = px.w / (widthMm / MM_PER_IN);
  const dy = px.h / (heightMm / MM_PER_IN);
  const dpi = Math.min(dx, dy);
  return { dpi, quality: qualityFor(dpi, settings) };
}

export function qualityFor(dpi: number, s: Pick<BuilderSettings, 'dpiGood' | 'dpiFair'>): Quality {
  if (!isFinite(dpi)) return 'vector';
  // a hair of tolerance: 299.6 DPI is 300 DPI to anyone printing it
  if (dpi >= s.dpiGood - 0.5) return 'good';
  if (dpi >= s.dpiFair - 0.5) return 'fair';
  return 'low';
}

/** Largest print width (mm) at which the artwork is still GOOD. */
export function maxGoodWidthMm(asset: Pick<AssetInfo, 'kind' | 'widthPx' | 'heightPx' | 'trim'>, trim: boolean, dpiGood: number): number {
  if (asset.kind !== 'raster') return Infinity;
  return (trimmedPx(asset, trim).w / dpiGood) * MM_PER_IN;
}

export const QUALITY_LABEL: Record<Quality, string> = {
  good: 'Good',
  fair: 'Fair',
  low: 'Low quality',
  vector: 'Vector'
};
